import React, { useState, useEffect } from "react";

// @material-ui/icons
import { Edit } from "components/MaterialSymbol/MaterialIcons";

// core components
import Button from "components/CustomButtons/Button.js";
import ReactTable from "components/ReactTable/ReactTable.js";
import Modal from "components/Modal/Modal.js";
import Notification from "components/Notification/Notification.js";
import AreYouSure from "components/AreYouSure/AreYouSure";

// custom components
import DealerCreateUser from "./DealerCreateUser";

import DealerUserService from "../../../services/DealerUserService";
import PasswordManagement from "components/PasswordManagement/PasswordManagement";
import DeleteButton from "components/DeleteButton/DeleteButton";
import PageLayout from "layouts/PageLayout";
import { safeParseList } from "helpers";


export const CREATE_USER = {
    title: "Create user",
    successMessage: "user created successfully",
};

export const EDIT_USER = {
    title: "Edit user",
    successMessage: "user updated successfully",
};

const DEALER_ADMIN_ROLE = 1;

export default function DealerUsers({ dlid, ...otherProps }) {
    const [open, setOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(CREATE_USER);
    const [openModal, setOpenModal] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const [notification, setNotification] = useState({
        open: false,
        color: "success",
    });

    useEffect(() => {
        const getUsers = async (dlid) => {
            const response = await DealerUserService.getAll(dlid);
            const users = response.data.map((user) => ({
                ...user,
                managedDomains: safeParseList(user.managedDomains),
            }));

            setUsers(users);
        };

        if (dlid) {
            getUsers(dlid);
        }
    }, [dlid]);

    const onSuccess = (user) => {
        if (form === CREATE_USER) {
            setUsers([...users, user]);
        }

        if (form === EDIT_USER) {
            const nUsers = users.map((c) => (c.id === user.id ? { ...c, ...user } : c));
            setUsers(nUsers);
        }

        setInitialValues({});
        setOpenModal(false);
        setNotification({
            ...notification,
            open: true,
            message: form.successMessage,
        });
    };

    return (
        <>
            {notification.open && (
                <Notification
                    open={notification.open}
                    color={notification.color}
                    message={notification.message}
                />
            )}

            <Modal open={openModal} modalTitle={form.title} handleClose={setOpenModal}>
                <DealerCreateUser
                    dlid={dlid}
                    form={form}
                    initialValues={initialValues}
                    onSuccess={(user) => onSuccess(user)}
                />
            </Modal>

            <PageLayout
                customComponent={
                    <>
                        <Button
                            color="primary"
                            onClick={() => {
                                setForm(CREATE_USER);
                                setOpenModal(true);
                                setInitialValues({});
                            }}
                        >
                            Create user
                        </Button>
                    </>
                }
                {...otherProps}
            >
                <ReactTable
                    columns={[
                        {
                            Header: "Id",
                            accessor: "id",
                        },
                        {
                            Header: "First name",
                            accessor: "firstName",
                        },
                        {
                            Header: "Last name",
                            accessor: "lastName",
                        },
                        {
                            Header: "Email",
                            accessor: "email",
                        },
                        {
                            Header: "Role",
                            accessor: "role",
                        },
                        {
                            Header: "Created at",
                            accessor: "createdAt",
                        },
                        {
                            Header: "Updated at",
                            accessor: "updatedAt",
                        },
                        {
                            Header: "Actions",
                            Cell: (props) => {
                                const rowIdx = props.row.id;
                                const user = users[rowIdx];

                                return (
                                    <>
                                        <div className="actions-right">
                                            <Button
                                                justIcon
                                                round
                                                simple
                                                color="warning"
                                                className="edit"
                                                onClick={() => {
                                                    setForm(EDIT_USER);
                                                    setOpenModal(true);
                                                    setInitialValues(user);
                                                }}
                                            >
                                                <Edit />
                                            </Button>
                                            <DeleteButton
                                                title="dealer user"
                                                handleClick={async () => {
                                                    await DealerUserService.del(user.id);
                                                    setUsers(
                                                        [...users].filter((u) => u.id !== user.id)
                                                    );
                                                    setNotification({
                                                        ...notification,
                                                        open: true,
                                                        message: "Successfully Deleted",
                                                    });
                                                }}
                                                disabled={user.roleId == DEALER_ADMIN_ROLE}
                                            />
                                            <PasswordManagement
                                                userId={user.id}
                                                userType="DEALER_USER"
                                                userName={user.email}
                                            />
                                        </div>
                                    </>
                                );
                            },
                        },
                    ]}
                    data={users}
                />
            </PageLayout>

            <AreYouSure
                open={open}
                title={`Are you sure?`}
                subtitle={`The record will be deleted`}
                onAccept={async () => {
                    await DealerUserService.del(initialValues.id);
                    setUsers([...users].filter((user) => user.id !== initialValues.id));
                    setOpen(false);
                }}
                onCancel={() => setOpen(false)}
            />
        </>
    );
}
